import hljs from 'highlight.js';
import darkThemeCss from 'highlight.js/styles/atom-one-dark.css?inline';
import lightThemeCss from 'highlight.js/styles/atom-one-light.css?inline';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import validUrl from 'valid-url';

import { sanitize } from '@/services/sanitize';

let currentHljsStyleElement: HTMLStyleElement | null = null;

export function loadHighlightTheme(isDark: boolean): void {
    const themeCss = isDark ? darkThemeCss : lightThemeCss;

    // Remove existing highlight.js style element if present
    if (currentHljsStyleElement) {
        currentHljsStyleElement.remove();
    }

    // Create and append new style element
    const style = document.createElement('style');
    style.id = 'hljs-theme';
    style.textContent = themeCss;
    document.head.appendChild(style);
    currentHljsStyleElement = style;
}

// App-relative link to another conversation, as returned by the agent's recall tools
// (see docs/specs/past-conversations-recall.md). Strict on purpose: the isUri check below is
// what keeps javascript: and data: hrefs out, and this must not widen it.
const CHAT_LINK =
    /^\/chat\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

function isUrlValid(urlString: string) {
    return CHAT_LINK.test(urlString) || !!validUrl.isUri(urlString);
}

function processMessage(message: string) {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    return message.replace(linkRegex, (match, p1, p2) => {
        if (isUrlValid(p2)) {
            return match;
        } else {
            return p1;
        }
    });
}

const renderer = new marked.Renderer();

renderer.code = ({ text, lang }) => {
    const { value, language } = hljs.highlightAuto(
        text,
        lang ? [lang] : undefined
    );
    const rawLanguage = (language || lang || 'text') as string;
    const safeLanguage = rawLanguage.replace(/[^a-z0-9_-]/gi, '') || 'text';
    return `
        <div class="code-block" dir="ltr" data-language="${safeLanguage}">
            <button class="code-copy-button" type="button" data-copy-state="idle" aria-label="Copy code">
                <svg id="copy-button-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-copy-icon lucide-copy"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                <svg id="copied-button-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check-icon lucide-check"><path d="M20 6 9 17l-5-5"/></svg>
            </button>
            <pre><code class="hljs ${safeLanguage}">${value}</code></pre>
        </div>
    `;
};

renderer.codespan = ({ text }) => {
    return `<code dir="ltr">${text}</code>`;
};

renderer.link = ({ href, title, text }) => {
    const isValid = !!href && (CHAT_LINK.test(href) || !!validUrl.isUri(href));
    const safeHref = isValid ? href : '#';
    const linkTitle = title ? ` title="${title}"` : '';
    return `<a href="${safeHref}"${linkTitle} target="_blank" rel="noopener noreferrer" class="text-primary underline hover:text-primary">${text}</a>`;
};

marked.setOptions({
    renderer,
    gfm: true,
    breaks: true,
    async: false
});

export function parseMessage(message: string): string {
    const processedMessage = processMessage(message);
    const parsed = marked.parse(processedMessage, { async: false }) as string;
    return sanitize(parsed);
}

function removeColgroup(html: string): string {
    return html.replace(/<colgroup[\s\S]*?<\/colgroup>/gi, '');
}

export function htmlToMarkdown(html: string): string {
    // Pour retirer la balise <colgroup> que turndown-plugin-gfm ne gère pas
    const cleanedHtml = removeColgroup(html);

    const turndownService = new TurndownService({
        headingStyle: 'atx',
        codeBlockStyle: 'fenced',
        hr: '---',
        bulletListMarker: '-'
    });

    // Permet de gérer les tableaux Markdown (entre autres)
    turndownService.use(gfm);

    // Ajoute une règle pour supprimer les retours à la ligne dans les cellules de tableau
    turndownService.addRule('tableCellP', {
        filter: (node: any) =>
            node.nodeName === 'P' &&
            node.parentNode &&
            (node.parentNode.nodeName === 'TD' ||
                node.parentNode.nodeName === 'TH'),
        replacement: (content: any) => {
            return content.trim();
        }
    });
    turndownService.addRule('strong', {
        filter: ['strong'],
        replacement: (content: any) => {
            return `<strong>${content}</strong>`;
        }
    });

    return turndownService.turndown(cleanedHtml);
}
//function to clean html from <thead>, <tbody>, <think> and lonely </p> tags
export function fixTableHtml(html: string): string {
    return html
        .replace(/<think>[\s\S]*?<\/think>/gi, '')
        .replace(/<\/p>\s*<p>/gi, '<p>');
}
